import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Upload, LogOut } from 'lucide-react';
import { Input, Button } from '../ui'; // Assuming Input and Button are already localized or accept translated strings
import { signOut } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { db, storage } from '../../lib/firebase';
import { Therapist } from '../../types/firestoreTypes';
import imageCompression from 'browser-image-compression';

interface FormData {
  displayName: string;
  phone: string;
  bookingLink: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Type for errors state to hold translation keys or specific error messages
type ErrorState = Record<string, string>;

const ProfileSettingsTab: React.FC = () => {
  const { t } = useTranslation(); // Get the t function
  const navigate = useNavigate();
  const { user, therapist } = useAuth();
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'uploading' | 'success' | 'error'
  >('idle');
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormData>({
    displayName: '',
    phone: '',
    bookingLink: '',
  });
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  // Store translation keys or specific error messages in errors state
  const [errors, setErrors] = useState<ErrorState>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // Store translation key for success message
  const [successMessageKey, setSuccessMessageKey] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (user && therapist) {
      setFormData({
        displayName: therapist.displayName || '',
        phone: therapist.phone || '',
        bookingLink: therapist.bookingLink || '',
      });
      setPhotoURL(therapist.profilePhotoUrl || user.photoURL || null);
    }
  }, [user, therapist]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors({ photo: 'settings.profile.errors.invalidFileType' }); // Use key
      return;
    }

    try {
      setUploadStatus('uploading');
      setErrors({});
      setSuccessMessageKey(null); // Clear success message

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        fileType: file.type as string,
      };

      const compressedFile = await imageCompression(file, options);
      await handlePhotoUpload(compressedFile);
    } catch (error) {
      console.error('Error compressing image:', error);
      setErrors({ photo: 'settings.profile.errors.imageProcessingFailed' }); // Use key
      setUploadStatus('error');
    }
  };

  const handlePhotoUpload = async (file: File | Blob) => {
    if (!user) return;

    try {
      const fileName = 'profile.jpg';
      const storageRef = ref(storage, `profilePhotos/${user.uid}/${fileName}`);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update Auth profile
      await updateProfile(user, { photoURL: downloadURL });

      // Update Firestore document
      const therapistRef = doc(db, 'therapists', user.uid);
      await updateDoc(therapistRef, {
        profilePhotoUrl: downloadURL,
      });

      setPhotoURL(downloadURL);
      setUploadStatus('success');
      setSuccessMessageKey('settings.profile.success.photoUpdate'); // Use key
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadStatus('error');
      setErrors({ photo: 'settings.profile.errors.photoUploadFailed' }); // Use key
    }
  };

  const validateBookingLink = (url: string): boolean => {
    if (!url) return true; // Empty URL is valid (optional field)

    try {
      const urlObject = new URL(url);
      return urlObject.protocol === 'http:' || urlObject.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setIsUpdating(true);
    setErrors({});
    setSuccessMessageKey(null);

    // Validate booking link
    if (formData.bookingLink && !validateBookingLink(formData.bookingLink)) {
      setErrors({ bookingLink: 'settings.profile.errors.invalidBookingLink' });
      setIsUpdating(false);
      return;
    }

    try {
      // Update Auth profile
      await updateProfile(user, {
        displayName: formData.displayName,
      });

      // Update Firestore document
      const therapistRef = doc(db, 'therapists', user.uid);
      const updateData: Partial<Therapist> = {
        displayName: formData.displayName,
        phone: formData.phone,
        bookingLink: formData.bookingLink,
      };
      await updateDoc(therapistRef, updateData);

      setSuccessMessageKey('settings.profile.success.profileUpdate'); // Use key
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ submit: 'settings.profile.errors.profileUpdateFailed' }); // Use key
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!user || !user.email) return;
    setIsChangingPassword(true);
    setErrors({});
    setSuccessMessageKey(null);

    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setErrors({
          confirmPassword: 'settings.profile.errors.passwordsDoNotMatch',
        }); // Use key
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setErrors({ newPassword: 'settings.profile.errors.passwordTooShort' }); // Use key
        return;
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordData.newPassword);

      setSuccessMessageKey('settings.profile.success.passwordUpdate'); // Use key
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setErrors({ currentPassword: 'settings.profile.errors.wrongPassword' }); // Use key
      } else {
        setErrors({ submit: 'settings.profile.errors.passwordChangeFailed' }); // Use key
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login'); // Navigate after sign out
    } catch (error) {
      console.error('Error signing out:', error);
      // Optionally set an error message key here if needed
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Helper to translate error messages from state
  const getErrorMessage = (field: string): string | undefined => {
    const errorKey = errors[field];
    return errorKey ? t(errorKey) : undefined;
  };

  return (
    <div className="space-y-8">
      {/* Translate success message on render */}
      {successMessageKey && (
        <div className="p-4 bg-green-50 text-green-700 rounded-lg">
          {t(successMessageKey)}
        </div>
      )}

      {/* Profile Photo Section */}
      <div className="bg-white rounded-lg border border-[#EAECF0] p-6">
        <h2 className="text-lg font-semibold text-[#101828] mb-6">
          {t('settings.profile.headings.profilePhoto')}
        </h2>
        <div className="flex items-center space-x-6">
          <div className="relative">
            <img
              // Use display name for avatar generation, profile for alt text
              src={
                photoURL ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  formData.displayName || 'User'
                )}&background=7F56D9&color=fff`
              }
              alt={t('settings.profile.alts.profilePhoto')} // Alt text
              className="w-24 h-24 rounded-full object-cover"
              onError={(e) => {
                // Basic fallback
                const target = e.target as HTMLImageElement;
                target.onerror = null; // prevent infinite loop
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  formData.displayName || 'U'
                )}&background=7F56D9&color=fff`;
              }}
            />
            {uploadStatus === 'uploading' && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent"></div>{' '}
                {/* Loading spinner */}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="secondary"
              onClick={handleUploadClick}
              disabled={uploadStatus === 'uploading'}
              leftIcon={<Upload className="w-4 h-4" />}
            >
              {t('settings.profile.buttons.uploadPhoto')}
            </Button>
            {/* Translate error message on render */}
            {getErrorMessage('photo') && (
              <p className="text-sm text-red-600">{getErrorMessage('photo')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Profile Information Section */}
      <div className="bg-white rounded-lg border border-[#EAECF0] p-6 space-y-6">
        <h2 className="text-lg font-semibold text-[#101828]">
          {t('settings.profile.headings.profileInfo')}
        </h2>
        <div className="space-y-4">
          <Input
            label={t('settings.profile.form.displayName')} // Translate label
            value={formData.displayName}
            onChange={(e) =>
              setFormData({ ...formData, displayName: e.target.value })
            }
            error={getErrorMessage('displayName')} // Translate error
          />
          <Input
            label={t('settings.profile.form.phone')} // Translate label
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            error={getErrorMessage('phone')} // Translate error
          />
          <Input
            type="url"
            label={t('settings.profile.form.bookingLink')}
            placeholder={t('settings.profile.form.bookingLinkPlaceholder')}
            value={formData.bookingLink}
            onChange={(e) =>
              setFormData({ ...formData, bookingLink: e.target.value })
            }
            error={getErrorMessage('bookingLink')}
          />
          {formData.bookingLink && (
            <p className="text-sm text-gray-600">
              {t('settings.profile.form.bookingLinkHelp')}
            </p>
          )}
          <Button
            variant="secondary"
            onClick={handleSaveProfile}
            isLoading={isUpdating}
            disabled={isUpdating}
          >
            {t('settings.profile.buttons.saveProfile')}
          </Button>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-lg border border-[#EAECF0] p-6 space-y-6">
        <h2 className="text-lg font-semibold text-[#101828]">
          {t('settings.profile.headings.changePassword')}
        </h2>
        <div className="space-y-4">
          <Input
            type="password"
            label={t('settings.profile.form.currentPassword')} // Translate label
            value={passwordData.currentPassword}
            onChange={(e) =>
              setPasswordData({
                ...passwordData,
                currentPassword: e.target.value,
              })
            }
            error={getErrorMessage('currentPassword')} // Translate error
          />
          <Input
            type="password"
            label={t('settings.profile.form.newPassword')} // Translate label
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, newPassword: e.target.value })
            }
            error={getErrorMessage('newPassword')} // Translate error
          />
          <Input
            type="password"
            label={t('settings.profile.form.confirmPassword')} // Translate label
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData({
                ...passwordData,
                confirmPassword: e.target.value,
              })
            }
            error={getErrorMessage('confirmPassword')} // Translate error
          />
          <Button
            variant="secondary"
            onClick={handlePasswordChange}
            isLoading={isChangingPassword}
            disabled={isChangingPassword}
          >
            {t('settings.profile.buttons.changePassword')}
          </Button>
          {/* Translate general submit error */}
          {getErrorMessage('submit') && (
            <p className="text-sm text-red-600">{getErrorMessage('submit')}</p>
          )}
        </div>
      </div>

      {/* Account Actions Section */}
      <div className="bg-white rounded-lg border border-[#EAECF0] p-6">
        <h2 className="text-lg font-semibold text-[#101828] mb-6">
          {t('settings.profile.headings.accountActions')}
        </h2>
        <Button
          variant="secondary"
          onClick={handleLogout}
          isLoading={isLoggingOut}
          disabled={isLoggingOut}
          leftIcon={<LogOut className="w-4 h-4" />}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          {t('settings.profile.buttons.logout')}
        </Button>
      </div>
    </div>
  );
};

export default ProfileSettingsTab;
